/**
 * Chat file-attachment constraints. These mirror the backend `POST /file`
 * limits (see apps/server/src/file/constants.ts + config defaults) so the
 * client rejects unsupported picks before an upload round-trip. Keep these in
 * sync with the server allowlist/size/count.
 */

/** MIME types the backend accepts for chat attachments. */
export const CHAT_ALLOWED_FILE_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/csv',
] as const;

export type ChatAllowedFileMimeType = (typeof CHAT_ALLOWED_FILE_MIME_TYPES)[number];

/** Max attachments per message (backend caps `file_ids` at 5). */
export const CHAT_MAX_FILES = 5;

/** Max bytes per file (backend default is 10 MiB). */
export const CHAT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * `accept` string for the file picker / drag-drop. Uses exact MIME types
 * (not `image/*`) so the OS dialog and our matcher filter to exactly the
 * supported set.
 */
export const CHAT_FILE_ACCEPT = [ ...CHAT_ALLOWED_FILE_MIME_TYPES, '.csv' ].join(',');

/** The allowed types, as named in a "wrong file type" toast. */
export const CHAT_ALLOWED_FILE_LABEL = 'PDF, CSV, PNG, JPG, GIF and WebP';

/**
 * What the file library takes: the chat types except CSV, which is a chat
 * attachment only (the server leaves CSVs out of the library list).
 */
export const LIBRARY_ALLOWED_FILE_MIME_TYPES = CHAT_ALLOWED_FILE_MIME_TYPES.filter((type) => type !== 'text/csv');

/** `accept` string for the library's file picker. */
export const LIBRARY_FILE_ACCEPT = LIBRARY_ALLOWED_FILE_MIME_TYPES.join(',');

/**
 * Gives a picked `.csv` file the `text/csv` type. Browsers report CSVs
 * inconsistently (Windows often says `application/vnd.ms-excel`, some say
 * nothing), so the extension decides.
 *
 * @param file - The picked file.
 *
 * @returns The file, retyped as `text/csv` when it is a CSV.
 */
export const normalizeUploadFile = (file: File): File => {
  if (!file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv') {
    return file;
  }
  return new File([ file ], file.name, { type: 'text/csv', lastModified: file.lastModified });
};

/**
 * The sizes the server keeps every uploaded image at (mirrors
 * `FILE_THUMBNAIL_SIZE` in apps/server/src/file/constants.ts): WebP fitted
 * inside a square of that many pixels, never enlarged. Ask for the smallest
 * size at least as large as the image is drawn at the screen's density. A
 * lightbox asks for no size, so it shows the original. Any other size, or an
 * image whose sizes were never made, is served as the original too.
 */
export const FILE_THUMBNAIL_SIZE = {
  /** A 40px attachment chip or a small avatar-like preview. */
  CHIP: 160,
  /** A grid tile or a small preview. */
  TILE: 480,
  /** An image inside a page. */
  INLINE: 960,
  /** A lightbox or a full-width image. */
  FULL: 1920,
} as const;

export type FileThumbnailSize = (typeof FILE_THUMBNAIL_SIZE)[keyof typeof FILE_THUMBNAIL_SIZE];
